'use client'

import { AnalyticsSource } from '@b-stock/fe-core/bstock-react'
import { ListingEnums } from '@b-stock/fe-core/locale'
import type {
  CategoryDtoCategoriesEnum,
  ItemConditionDtoItemConditionsEnum,
} from '@b-stock/listing-api-client'

import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

export type SimilarListingsVariant = 'sidebar' | 'section' | 'card'

export interface SimilarListingsProps {
  searchQuery?: CategoryDtoCategoriesEnum
  condition: ItemConditionDtoItemConditionsEnum[]
  categories?: CategoryDtoCategoriesEnum[]
  promoteOtherListings?: 'all' | 'own' | 'none'
  storefront?: { id: string; name: string }
  variant: SimilarListingsVariant
  screenName?: string
}

export function useSearchSimilarListingsHandler(
  params: SimilarListingsProps
): () => void {
  const {
    searchQuery,
    condition,
    categories,
    promoteOtherListings,
    storefront,
    screenName = 'listing_detail_page',
    variant,
  } = params

  const formatItemCondition = ListingEnums.ItemCondition.useFormat()
  const formatSubcategory = ListingEnums.Subcategory.useFormat()
  const { trackButtonClicked } = useAnalytics()

  return () => {
    const formattedQuery = searchQuery ? formatSubcategory(searchQuery) : ''

    const urlParams = new URLSearchParams()

    if (formattedQuery) {
      urlParams.set('q', formattedQuery)
      urlParams.set('o', 'desc')
      urlParams.set('s', 'relevance')

      if (condition.length > 0) {
        const displayNames = condition.map(formatItemCondition)
        urlParams.set('condition', JSON.stringify(displayNames))
      }

      if (promoteOtherListings === 'own' && storefront) {
        const storefrontValue = `${storefront.id}-${storefront.name}`
        urlParams.set('storefront', JSON.stringify([storefrontValue]))
      }
    }

    const bstockCondition =
      condition.length > 0
        ? condition.map((cond) => ({
            condition: formatItemCondition(cond),
            '%': `${(100 / condition.length).toFixed(1)}%`,
          }))
        : []

    const bstockCategory =
      categories && categories.length > 0
        ? categories.map((cat) => formatSubcategory(cat))
        : []

    trackButtonClicked({
      buttonName: 'similar_listings',
      search_term: formattedQuery,
      bstock_condition: bstockCondition,
      bstock_category: bstockCategory,
      storefront_name: storefront?.name || '',
      screenName,
      source: AnalyticsSource.HOME_PORTAL,
      variant,
    })

    const paramString = urlParams.toString()
    const url = paramString ? `/all-auctions?${paramString}` : '/all-auctions'
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}
